import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { errorFields, errorMessage, hasCode } from '../lib/api-error';
import { useAuth } from '../lib/auth-context';
import { dateTime } from '../lib/format';
import { isReadOnly } from '../lib/permissions';
import { toast } from '../lib/toast';
import {
  CONTACT_LANGUAGES,
  CONTACT_LANGUAGE_LABELS,
  COUNTRY_CHOICES,
  CUSTOMER_TYPE_CHOICES,
  CUSTOMER_TYPE_LABELS,
  VAT_TREATMENT_CHOICES,
  countryChoice,
  countryLabel,
  useContact,
  useCreateContact,
  useDeleteContact,
  useUpdateContact,
  useViesCheck,
  vatTreatmentCountryConflict,
  type Contact,
  type ContactLanguage,
  type ContactWrite,
  type CreateContactBody,
  type CustomerType,
  type VatTreatment,
  type ViesCheckRecord,
  type ViesCheckResponse,
} from '../lib/contacts-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  type BadgeTone,
} from '../components/ui';

/**
 * Cari detayı — düzenleme formu, VIES kontrolü, silme/pasifleştirme.
 *
 * Bu dosya ayrıca "Yeni cari" kutusunu (`NewContactModal`) ve iki formun
 * ORTAK alanlarını taşır. Ortaklık bilinçli: `customerType`,
 * `filesPeriodicReturns` ve `defaultVatTreatment` alanlarının altındaki
 * hukuki açıklamalar iki yerde kopyalansaydı biri düzeltilip öteki
 * unutulurdu — ve o metinler operatörün yanlış Intervat kutusu üretmesini
 * engelleyen tek uyarıdır.
 */

// ────────────────────────────────────────────────────────────── form durumu

interface ContactForm {
  name: string;
  legalName: string;
  isCustomer: boolean;
  isSupplier: boolean;
  customerType: CustomerType | '';
  vatNumber: string;
  enterpriseNumber: string;
  countryCode: string;
  filesPeriodicReturns: boolean;
  language: ContactLanguage;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  countrySubdivision: string;
  peppolParticipantId: string;
  /**
   * ⚠ METİN olarak tutulur. Sınıra giden sayısal alanların tamamı bu panelde
   * metin state'te durur; para ve oranlarda bu bir ZORUNLULUKTUR (IEEE-754
   * kaybı tam sınırda olurdu) ve gün sayısında da aynı kalıp korunur ki
   * "hangi alan sayı, hangisi metin" diye düşünülmesin. Sunucuya gönderilirken
   * yalnızca burada, deseni doğrulandıktan sonra tam sayıya çevrilir.
   */
  paymentTermDays: string;
  iban: string;
  bic: string;
  email: string;
  phone: string;
  einvoiceEmailAddress: string;
  einvoiceEmailConsentAt: string;
  einvoiceEmailConsentNote: string;
  defaultVatTreatment: VatTreatment | '';
  notes: string;
  active: boolean;
}

function blankForm(): ContactForm {
  return {
    name: '',
    legalName: '',
    isCustomer: true,
    isSupplier: false,
    customerType: '',
    vatNumber: '',
    enterpriseNumber: '',
    // Charleroi'daki bir işletmenin carilerinin çoğu Belçikalı olacak.
    countryCode: 'BE',
    filesPeriodicReturns: false,
    language: 'fr',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    countrySubdivision: '',
    peppolParticipantId: '',
    paymentTermDays: '30',
    iban: '',
    bic: '',
    email: '',
    phone: '',
    einvoiceEmailAddress: '',
    einvoiceEmailConsentAt: '',
    einvoiceEmailConsentNote: '',
    defaultVatTreatment: '',
    notes: '',
    active: true,
  };
}

function formFrom(contact: Contact): ContactForm {
  return {
    name: contact.name,
    legalName: contact.legalName ?? '',
    isCustomer: contact.isCustomer,
    isSupplier: contact.isSupplier,
    customerType: contact.customerType ?? '',
    vatNumber: contact.vatNumber ?? '',
    enterpriseNumber: contact.enterpriseNumber ?? '',
    countryCode: contact.countryCode,
    filesPeriodicReturns: contact.filesPeriodicReturns,
    language: contact.language,
    addressLine1: contact.addressLine1 ?? '',
    addressLine2: contact.addressLine2 ?? '',
    postalCode: contact.postalCode ?? '',
    city: contact.city ?? '',
    countrySubdivision: contact.countrySubdivision ?? '',
    peppolParticipantId: contact.peppolParticipantId ?? '',
    paymentTermDays: String(contact.paymentTermDays),
    iban: contact.iban ?? '',
    bic: contact.bic ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    einvoiceEmailAddress: contact.einvoiceEmailAddress ?? '',
    einvoiceEmailConsentAt: contact.einvoiceEmailConsentAt ?? '',
    einvoiceEmailConsentNote: contact.einvoiceEmailConsentNote ?? '',
    defaultVatTreatment: contact.defaultVatTreatment ?? '',
    notes: contact.notes ?? '',
    active: contact.active,
  };
}

/** Boş metin `null` demektir: "alanı temizle". Boş dize DB'ye YAZILMAZ. */
function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** `0`–`365` arası tam sayı. Metin state'ten sınıra geçişin TEK yeri. */
const PAYMENT_DAYS_PATTERN = /^\d{1,3}$/;

function paymentDaysValid(value: string): boolean {
  const trimmed = value.trim();
  if (!PAYMENT_DAYS_PATTERN.test(trimmed)) return false;
  const days = Number(trimmed);
  return days >= 0 && days <= 365;
}

/**
 * YALNIZCA değişen alanları döndürür.
 *
 * Tüm formu göndermek iki somut zarar verirdi: (1) denetim kaydına her
 * kaydetmede yirmi alan "değişti" diye yazılır ve gerçek değişiklik
 * kaybolurdu, (2) `peppolParticipantId`i hiç açmadan göndermek sunucunun
 * "operatör AÇIKÇA yazdı, dokunma" dalını tetikler ve işletme numarası
 * düzeltilse bile Peppol kimliği eski numarada takılı kalırdı.
 */
function buildPatch(form: ContactForm, contact: Contact): ContactWrite {
  const patch: ContactWrite = {};

  if (form.name.trim() !== contact.name) patch.name = form.name.trim();
  if (form.countryCode.trim().toUpperCase() !== contact.countryCode) {
    patch.countryCode = form.countryCode.trim().toUpperCase();
  }
  if (nullable(form.legalName) !== contact.legalName) patch.legalName = nullable(form.legalName);
  if (form.isCustomer !== contact.isCustomer) patch.isCustomer = form.isCustomer;
  if (form.isSupplier !== contact.isSupplier) patch.isSupplier = form.isSupplier;

  const customerType = form.customerType === '' ? null : form.customerType;
  if (customerType !== contact.customerType) patch.customerType = customerType;

  /*
   * ⚠ Karşılaştırma HAM metinle yapılır ama sunucu değeri KANONİKLEŞTİRİR
   * ('BE 1041.328.256' → 'BE1041328256'). Operatör numarayı ayraçlı yeniden
   * yazarsa alan "değişti" sayılır ve gereksiz bir yazma olur — zararsızdır;
   * tersi (kanonikle karşılaştırıp gerçek değişikliği kaçırmak) zararlı olurdu.
   */
  if (nullable(form.vatNumber) !== contact.vatNumber) patch.vatNumber = nullable(form.vatNumber);
  if (nullable(form.enterpriseNumber) !== contact.enterpriseNumber) {
    patch.enterpriseNumber = nullable(form.enterpriseNumber);
  }
  if (form.filesPeriodicReturns !== contact.filesPeriodicReturns) {
    patch.filesPeriodicReturns = form.filesPeriodicReturns;
  }
  if (form.language !== contact.language) patch.language = form.language;

  if (nullable(form.addressLine1) !== contact.addressLine1) {
    patch.addressLine1 = nullable(form.addressLine1);
  }
  if (nullable(form.addressLine2) !== contact.addressLine2) {
    patch.addressLine2 = nullable(form.addressLine2);
  }
  if (nullable(form.postalCode) !== contact.postalCode)
    patch.postalCode = nullable(form.postalCode);
  if (nullable(form.city) !== contact.city) patch.city = nullable(form.city);
  if (nullable(form.countrySubdivision) !== contact.countrySubdivision) {
    patch.countrySubdivision = nullable(form.countrySubdivision);
  }
  if (nullable(form.peppolParticipantId) !== contact.peppolParticipantId) {
    patch.peppolParticipantId = nullable(form.peppolParticipantId);
  }

  if (form.paymentTermDays.trim() !== String(contact.paymentTermDays)) {
    patch.paymentTermDays = Number(form.paymentTermDays.trim());
  }

  if (nullable(form.iban) !== contact.iban) patch.iban = nullable(form.iban);
  if (nullable(form.bic) !== contact.bic) patch.bic = nullable(form.bic);
  if (nullable(form.email) !== contact.email) patch.email = nullable(form.email);
  if (nullable(form.phone) !== contact.phone) patch.phone = nullable(form.phone);

  if (nullable(form.einvoiceEmailAddress) !== contact.einvoiceEmailAddress) {
    patch.einvoiceEmailAddress = nullable(form.einvoiceEmailAddress);
  }
  if (nullable(form.einvoiceEmailConsentAt) !== contact.einvoiceEmailConsentAt) {
    patch.einvoiceEmailConsentAt = nullable(form.einvoiceEmailConsentAt);
  }
  if (nullable(form.einvoiceEmailConsentNote) !== contact.einvoiceEmailConsentNote) {
    patch.einvoiceEmailConsentNote = nullable(form.einvoiceEmailConsentNote);
  }

  const treatment = form.defaultVatTreatment === '' ? null : form.defaultVatTreatment;
  if (treatment !== contact.defaultVatTreatment) patch.defaultVatTreatment = treatment;

  if (nullable(form.notes) !== contact.notes) patch.notes = nullable(form.notes);
  if (form.active !== contact.active) patch.active = form.active;

  return patch;
}

/**
 * Oluşturma gövdesi. Boş bırakılan metin alanları HİÇ GÖNDERİLMEZ — yeni
 * kayıtta `null` yazmakla alanı hiç göndermemek aynı sonucu verir ve gövdeyi
 * okunur tutmak denetim kaydını da okunur tutar.
 */
function buildCreateBody(form: ContactForm): CreateContactBody {
  const body: CreateContactBody = {
    name: form.name.trim(),
    countryCode: form.countryCode.trim().toUpperCase(),
    isCustomer: form.isCustomer,
    isSupplier: form.isSupplier,
    filesPeriodicReturns: form.filesPeriodicReturns,
    language: form.language,
    paymentTermDays: Number(form.paymentTermDays.trim()),
  };

  if (form.customerType !== '') body.customerType = form.customerType;
  if (form.defaultVatTreatment !== '') body.defaultVatTreatment = form.defaultVatTreatment;

  const legalName = nullable(form.legalName);
  if (legalName) body.legalName = legalName;
  const vatNumber = nullable(form.vatNumber);
  if (vatNumber) body.vatNumber = vatNumber;
  const enterpriseNumber = nullable(form.enterpriseNumber);
  if (enterpriseNumber) body.enterpriseNumber = enterpriseNumber;
  const email = nullable(form.email);
  if (email) body.email = email;
  const phone = nullable(form.phone);
  if (phone) body.phone = phone;
  const city = nullable(form.city);
  if (city) body.city = city;

  return body;
}

// ───────────────────────────────────────────────────────── alan hata metni

/**
 * Sunucunun `fields` dizisindeki alan adı → alan altına yazılacak cümle.
 *
 * ⚠ Sunucunun `message` alanı ekrana ÇIKMAZ (dil bağımsız değil, 5xx'te boş).
 * Genel cümle `errorMessage(err)` ile toast'ta gösterilir; buradaki metin
 * yalnızca HANGİ kutunun kırmızı olduğunu ve NİÇİN reddedildiğini söyler.
 */
const FIELD_HINTS: Record<string, string> = {
  name: 'Ad zorunludur.',
  countryCode: 'İki harfli ülke kodu girin (ISO 3166-1 alpha-2).',
  vatNumber: 'Numara hiçbir AB üye devletinin biçimine uymuyor ya da başka bir caride kayıtlı.',
  enterpriseNumber: 'İşletme numarası 10 hane olmalı; Belçikalı caride mod-97 sağlaması tutmalı.',
  iban: 'IBAN sağlaması tutmuyor.',
  bic: 'BIC 8 ya da 11 karakter olmalı.',
  email: 'Geçerli bir e-posta adresi girin.',
  paymentTermDays: 'Gün sayısı 0–365 arası bir tam sayı olmalı.',
  isCustomer: 'Cari en az müşteri ya da tedarikçi olmalı.',
  isSupplier: 'Cari en az müşteri ya da tedarikçi olmalı.',
  peppolParticipantId: 'Peppol kimliği kabul edilmedi.',
  einvoiceEmailAddress: 'Geçerli bir e-posta adresi girin.',
  einvoiceEmailConsentAt: 'Onay tarihi YYYY-AA-GG biçiminde ve bugünden ileri OLMAMALI.',
  einvoiceEmailConsentNote: 'Onayın nerede verildiğini yazın (sözleşme maddesi, e-posta tarihi).',
};

function fieldError(invalid: Set<string>, name: string): string | null {
  if (!invalid.has(name)) return null;
  return FIELD_HINTS[name] ?? 'Bu alan sunucu tarafından reddedildi.';
}

// ─────────────────────────────────────────────────────── ortak form alanları

interface FieldsProps {
  form: ContactForm;
  set: <K extends keyof ContactForm>(key: K, value: ContactForm[K]) => void;
  invalid: Set<string>;
  disabled: boolean;
}

const COUNTRY_LIST_ID = 'centramir-country-codes';

/** Ülke kodu girişi — datalist ile yardım, serbest giriş korunur. */
function CountryField({ form, set, invalid, disabled }: FieldsProps) {
  const choice = countryChoice(form.countryCode);
  const code = form.countryCode.trim().toUpperCase();

  return (
    <div>
      <Input
        label="Ülke kodu"
        list={COUNTRY_LIST_ID}
        value={form.countryCode}
        maxLength={2}
        disabled={disabled}
        // Sunucu `^[A-Za-z]{2}$` bekler ve büyük harfe çevirir; aynı dönüşümü
        // burada yapmak operatörün gördüğü değerle kaydedilen değeri eşitler.
        onChange={(e) => set('countryCode', e.target.value.toUpperCase())}
        error={fieldError(invalid, 'countryCode')}
        hint={
          choice
            ? `${choice.label} — ${choice.eu ? 'AB KDV alanı' : 'AB dışı'}`
            : code.length === 2
              ? 'Listede olmayan bir kod; sunucu iki harfli her kodu kabul eder.'
              : 'İki harfli ülke kodu (BE, FR, DE…).'
        }
      />
      <datalist id={COUNTRY_LIST_ID}>
        {COUNTRY_CHOICES.map((c) => (
          <option key={c.code} value={c.code} label={c.label} />
        ))}
      </datalist>
      {code === 'GR' && (
        <p className="mt-1 text-xs text-[var(--c-warn)]">
          ⚠ Yunanistan'ın ülke kodu <strong>GR</strong>, KDV numarası öneki <strong>EL</strong>'dir.
          Numarayı <code>EL…</code> olarak yazın, yoksa VIES bulamaz.
        </p>
      )}
      {code === 'XI' && (
        <p className="mt-1 text-xs text-[var(--c-ink-soft)]">
          Kuzey İrlanda yarım AB'dir: <strong>mal</strong> teslimi AB içi (kutu 46),{' '}
          <strong>hizmet</strong> AB dışıdır (kutu 47).
        </p>
      )}
    </div>
  );
}

/**
 * KDV çözümleyicisinin cari tarafı girdileri.
 *
 * Bu üç alan (`customerType`, `countryCode`, `filesPeriodicReturns`) + KDV
 * numarası olmadan kutu 3 / 44 / 46 ayrımı YAPILAMAZ. Yanlış değer sessizce
 * yanlış kutuya yazar; hata ancak Intervat dosyası reddedildiğinde — ya da
 * hiç reddedilmeyip eksik beyan olarak kalarak — görünür.
 */
function VatQualificationFields({ form, set, invalid, disabled }: FieldsProps) {
  const selected = CUSTOMER_TYPE_CHOICES.find((c) => c.value === form.customerType) ?? null;
  const isBelgian = form.countryCode.trim().toUpperCase() === 'BE';
  const hasVatNumber = form.vatNumber.trim() !== '';

  return (
    <div className="space-y-3">
      <div>
        <Select
          label="Müşteri tipi"
          value={form.customerType}
          disabled={disabled}
          onChange={(e) => set('customerType', e.target.value as CustomerType | '')}
        >
          <option value="">— seçilmedi —</option>
          {CUSTOMER_TYPE_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
        {/*
          ⚠ SEÇİLMEMİŞ TİPİN SONUCU, KDV NUMARASININ VARLIĞINA GÖRE DEĞİŞİR ve
          ikisini aynı cümleyle anlatmak yanlış olur:

           · KDV numarası VARSA fatura DÜZENLENEMEZ
             (`INVOICE_CUSTOMER_TYPE_UNRESOLVED`). Sunucu burada bilerek
             varsayıma düşmüyor: KDV numarası taşıyan bir cari tanımı gereği
             mükelleftir, "tüketici" saymak kendi içinde çelişkilidir. Ve
             sonucu KDV tarafında görünmez — yurtiçinde iki hâlde de %21,
             kutu 3 — asıl fark belgenin Peppol'den geçmek ZORUNDA olup
             olmadığıdır (md. 53 §2bis).

           · KDV numarası YOKSA cari tüketici sayılır ve fatura kesilir; bu
             meşrudur, numarası olmayan bir cari gerçekten tüketici olabilir.
        */}
        <p className="mt-1 text-xs text-[var(--c-ink-soft)]">
          {selected ? (
            selected.note
          ) : hasVatNumber ? (
            <span className="text-[var(--c-danger)]">
              KDV numarası girilmiş ama tip seçilmemiş: bu cariye fatura DÜZENLENEMEZ. KDV numarası
              taşıyan bir cari tüketici sayılamaz ve tip, belgenin Peppol’den geçmek zorunda olup
              olmadığını belirler.
            </span>
          ) : (
            'Seçilmezse cari TÜKETİCİ sayılır (yurtiçinde yine %21, kutu 3). KDV numarası girilirse tip seçmek ZORUNLU olur.'
          )}
        </p>
      </div>

      {/*
        ⚠ Kutu yabancı caride de AÇIK BIRAKILIR, kilitlenmez. İki sebep:
          1. Kilitlemek yanlış olurdu — Belçika'da KDV kaydı olan yabancı bir
             mükellef de periyodik beyan verebilir; alan "kurulu olduğu ülke"
             değil, "Belçika'da beyan veriyor mu" sorusudur.
          2. Kilitli bir kutu ESKİ DEĞERİ taşır: BE'yken işaretlenmiş bir cari
             FR'ye çevrildiğinde işaret pasif ama TRUE kalır, sonra ülke BE'ye
             dönerse md. 20 şartı operatör hiç onaylamadan sağlanmış görünür.
      */}
      <Checkbox
        label="Belçika'da periyodik KDV beyanı veriyor"
        disabled={disabled}
        checked={form.filesPeriodicReturns}
        onChange={(e) => set('filesPeriodicReturns', e.target.checked)}
        hint={
          isBelgian
            ? 'Yurtiçi taşınmaz işi ters yükümlülüğünün (md. 20 KB nr. 1) ŞARTI budur. ' +
              'Sağlanmıyorsa aynı iş için normal %21 uygulanır.'
            : 'Yalnız Belçika’da beyan veren alıcıda anlamlıdır (md. 20 KB nr. 1). ' +
              'Yurtdışı carilerin çoğunda işaretsiz kalır.'
        }
      />

      <Input
        label="KDV numarası"
        value={form.vatNumber}
        disabled={disabled}
        placeholder="BE1041328256"
        onChange={(e) => set('vatNumber', e.target.value)}
        error={fieldError(invalid, 'vatNumber')}
        hint="Serbest yazın, sunucu ayraçları temizler ve ülke önekiyle kanonikleştirir."
      />

      <Input
        label="İşletme numarası (KBO/BCE)"
        value={form.enterpriseNumber}
        disabled={disabled}
        placeholder="1041328256"
        onChange={(e) => set('enterpriseNumber', e.target.value)}
        error={fieldError(invalid, 'enterpriseNumber')}
        hint="10 hane. Belçikalı caride mod-97 sağlaması aranır; Peppol kimliği de buradan türer."
      />
    </div>
  );
}

/** Kimlik ve rol — iki formun da ilk bölümü. */
function IdentityFields({ form, set, invalid, disabled }: FieldsProps) {
  return (
    <div className="space-y-3">
      <Input
        label="Ad"
        value={form.name}
        disabled={disabled}
        onChange={(e) => set('name', e.target.value)}
        error={fieldError(invalid, 'name')}
        hint="Panelde aranan ticari ad."
      />

      <Input
        label="Resmî unvan"
        value={form.legalName}
        disabled={disabled}
        onChange={(e) => set('legalName', e.target.value)}
        hint="Faturaya BU basılır; boşsa yukarıdaki ad kullanılır."
      />

      <div className="rounded-[var(--r-md)] border border-[var(--c-border)] px-3 py-2">
        <Checkbox
          label="Müşteri"
          disabled={disabled}
          checked={form.isCustomer}
          onChange={(e) => set('isCustomer', e.target.checked)}
        />
        <Checkbox
          label="Tedarikçi"
          disabled={disabled}
          checked={form.isSupplier}
          onChange={(e) => set('isSupplier', e.target.checked)}
        />
        {(fieldError(invalid, 'isCustomer') ?? fieldError(invalid, 'isSupplier')) && (
          <p className="mt-1 text-xs text-[var(--c-danger)]">
            Cari en az müşteri ya da tedarikçi olmalı.
          </p>
        )}
        {!form.isCustomer && !form.isSupplier && (
          <p className="mt-1 text-xs text-[var(--c-warn)]">
            En az biri işaretli olmalı — rolsüz cari kaydedilemez.
          </p>
        )}
      </div>

      <CountryField form={form} set={set} invalid={invalid} disabled={disabled} />

      <Select
        label="İletişim dili"
        value={form.language}
        disabled={disabled}
        onChange={(e) => set('language', e.target.value as ContactLanguage)}
        hint="⚠ Yalnız yazışma dilidir. FATURA DİLİ bundan bağımsızdır: onu işletme merkezinin bölgesi belirler (Charleroi → Valonya → Fransızca)."
      >
        {CONTACT_LANGUAGES.map((code) => (
          <option key={code} value={code}>
            {CONTACT_LANGUAGE_LABELS[code]}
          </option>
        ))}
      </Select>
    </div>
  );
}

/**
 * Gelişmiş: KDV muamelesi override'ı.
 *
 * ⚠ Bu alan OTOMATİK ÇÖZÜMÜ EZER. Ayrı bir bölümde durmasının sebebi, günlük
 * kullanımda hiç dokunulmaması gereken bir alan olmasıdır: doğru çözüm
 * neredeyse her zaman müşteri tipini ve ülkeyi düzeltmektir, muameleyi elle
 * seçmek değil.
 */
function AdvancedVatField({ form, set, disabled }: Omit<FieldsProps, 'invalid'>) {
  const selected = VAT_TREATMENT_CHOICES.find((c) => c.value === form.defaultVatTreatment) ?? null;
  const conflict = vatTreatmentCountryConflict(
    form.countryCode,
    form.defaultVatTreatment === '' ? null : form.defaultVatTreatment,
  );

  return (
    <div className="space-y-2">
      <Select
        label="KDV muamelesi (override)"
        value={form.defaultVatTreatment}
        disabled={disabled}
        onChange={(e) => set('defaultVatTreatment', e.target.value as VatTreatment | '')}
      >
        <option value="">Otomatik — önerilen</option>
        {VAT_TREATMENT_CHOICES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </Select>

      {selected ? (
        <p className="text-xs text-[var(--c-ink-soft)]">{selected.note}</p>
      ) : (
        <p className="text-xs text-[var(--c-ink-soft)]">
          Boş bırakın. Çözümleyici muameleyi müşteri tipi, ülke, VIES durumu ve kalemin niteliğinden
          türetir.
        </p>
      )}

      <p className="text-xs text-[var(--c-warn)]">
        ⚠ Bu seçim otomatik çözümü EZER ve yanlış seçim YANLIŞ INTERVAT KUTUSU üretir. Override
        yalnız muamele kararını değiştirir; işletme numarası, Belçika IBAN'ı ve belge dili gibi sert
        doğrulayıcıları ASLA atlatamaz.
      </p>

      {conflict && (
        <p className="rounded-[var(--r-md)] bg-[var(--c-danger-soft)] px-3 py-2 text-xs text-[var(--c-danger)]">
          {conflict}
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────── VIES paneli

interface ViesView {
  tone: BadgeTone;
  badge: string;
  title: string;
  body: string;
  checkedAt: string | null;
  requestIdentifier: string | null;
  viesName: string | null;
  viesAddress: string | null;
  error: string | null;
}

/**
 * TAZE sorgu sonucu → ekran durumu.
 *
 * ⚠ ÜÇ DURUM AYRIDIR ve karıştırılmaz:
 *   · geçerli    → yeşil,
 *   · bulunamadı → kırmızı (numara GERÇEKTEN yok),
 *   · ulaşılamadı → sarı, HATA DEĞİL.
 * Üye devlet servisleri düzenli olarak kapanır; "ulaşılamadı"yı kırmızı
 * göstermek gerçek bir kurumsal müşteriyi sahte numaralı gibi gösterir ve
 * operatörü meşru bir ters yükümlülük faturasını kesmemeye iter.
 */
function viewFromResponse(res: ViesCheckResponse): ViesView {
  const base = {
    checkedAt: res.checkedAt,
    requestIdentifier: res.requestIdentifier,
    viesName: res.name,
    viesAddress: res.address,
    error: res.error,
  };

  switch (res.outcome) {
    case 'valid':
      return {
        ...base,
        tone: 'ok',
        badge: 'Geçerli',
        title: 'KDV numarası VIES tarafından doğrulandı.',
        body: 'Numara atanmış ve sorgu anında aktif.',
      };
    case 'not_found':
      return {
        ...base,
        tone: 'danger',
        badge: 'Bulunamadı',
        title: 'VIES cevap verdi: böyle bir numara atanmamış.',
        body: 'Bu KESİN bir olumsuz cevaptır. Numarayı müşteriden teyit edin.',
      };
    case 'invalid_format':
      return {
        ...base,
        tone: 'danger',
        badge: 'Biçim geçersiz',
        title: 'Numara hiçbir üye devletin biçimine uymuyor.',
        body: 'Sorgu VIES’e hiç gitmedi ya da girdi reddedildi; bu da KESİN bir olumsuzluktur.',
      };
    case 'unavailable':
      return {
        ...base,
        tone: 'warn',
        badge: 'Ulaşılamadı',
        title: 'VIES’e ulaşılamadı — bu bir HATA DEĞİLDİR.',
        body:
          'Üye devlet servisleri düzenli olarak kapanır. Numara hakkında BİLGİ YOK demektir; ' +
          '"geçersiz" demek değildir. Bir süre sonra tekrar deneyin.',
      };
    case 'no_number':
      return {
        ...base,
        tone: 'neutral',
        badge: 'Numara yok',
        title: 'Sorulacak bir KDV numarası yok.',
        body: 'B2C ya da numarasız cari. Ters yükümlülük dalı zaten uygulanmaz.',
      };
  }
}

/**
 * Cariye YAZILMIŞ son kayıt → ekran durumu.
 *
 * ⚠ `valid: null` = SORULAMADI, `valid: false` = SORULDU VE GEÇERSİZ.
 * Kayıt "bulunamadı" ile "biçim bozuk" ayrımını taşımaz (ikisi de `false`);
 * bu yüzden metin ikisini birden kapsayacak şekilde yazılmıştır.
 */
function viewFromRecord(record: ViesCheckRecord): ViesView {
  const base = {
    checkedAt: record.checkedAt,
    requestIdentifier: record.requestIdentifier,
    viesName: record.name,
    viesAddress: record.address,
    error: record.error,
  };

  if (record.valid === true) {
    return {
      ...base,
      tone: 'ok',
      badge: 'Geçerli',
      title: 'Son sorguda numara geçerliydi.',
      body: 'Bu bir ANLIK durumdur; teslim anındaki kanıt faturaya ayrıca donar.',
    };
  }
  if (record.valid === false) {
    return {
      ...base,
      tone: 'danger',
      badge: 'Geçersiz',
      title: 'Son sorguda VIES bu numarayı geçerli bulmadı.',
      body: 'KESİN bir olumsuz cevap. Numarayı müşteriden teyit edin.',
    };
  }
  return {
    ...base,
    tone: 'warn',
    badge: 'Ulaşılamadı',
    title: 'Son sorguda VIES’e ulaşılamadı — HATA DEĞİL.',
    body: 'Numara hakkında bilgi yok demektir; "geçersiz" demek değildir.',
  };
}

function ViesPanel({ contact, canWrite }: { contact: Contact; canWrite: boolean }) {
  const check = useViesCheck();
  const fresh = check.data ?? null;

  const view = fresh
    ? viewFromResponse(fresh)
    : contact.viesCheck
      ? viewFromRecord(contact.viesCheck)
      : null;

  const run = (force: boolean) => {
    check.mutate(
      { id: contact.id, force },
      {
        // ⚠ `unavailable` buraya DÜŞMEZ: sunucu onu 200 ile döndürür. Buraya
        // yalnız gerçek arızalar (ağ, yetki, cari yok) düşer.
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title="VIES doğrulaması"
        description="AB KDV numarasının gerçekten atanmış olup olmadığı."
      />
      <div className="space-y-3 px-4 py-4">
        {contact.vatNumber === null && (
          <p className="text-xs text-[var(--c-ink-soft)]">
            Bu caride KDV numarası yok. Sorgu yapılabilir ama sonuç "numara yok" olur.
          </p>
        )}

        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" loading={check.isPending} onClick={() => run(false)}>
              KDV numarasını doğrula
            </Button>
            <Button
              size="sm"
              variant="secondary"
              loading={check.isPending}
              onClick={() => run(true)}
              title="24 saatlik kaydı atlar ve VIES'e yeniden sorar."
            >
              Önbelleği atlayarak sor
            </Button>
          </div>
        )}

        {view === null ? (
          <p className="text-sm text-[var(--c-ink-soft)]">Bu cari için henüz sorgu yapılmadı.</p>
        ) : (
          <div
            className={
              view.tone === 'ok'
                ? 'rounded-[var(--r-md)] bg-[var(--c-ok-soft)] px-3 py-3'
                : view.tone === 'danger'
                  ? 'rounded-[var(--r-md)] bg-[var(--c-danger-soft)] px-3 py-3'
                  : view.tone === 'warn'
                    ? 'rounded-[var(--r-md)] bg-[var(--c-warn-soft)] px-3 py-3'
                    : 'rounded-[var(--r-md)] bg-[var(--c-surface-alt)] px-3 py-3'
            }
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={view.tone}>{view.badge}</Badge>
              {fresh?.fromCache && <Badge tone="neutral">24 saatlik kayıttan</Badge>}
              {!fresh && <Badge tone="neutral">kayıtlı sonuç</Badge>}
            </div>

            <p className="mt-2 text-sm font-medium text-[var(--c-ink)]">{view.title}</p>
            <p className="mt-1 text-xs text-[var(--c-ink-soft)]">{view.body}</p>

            <dl className="mt-3 space-y-1 text-xs">
              <Row label="Sorgu zamanı">{dateTime(view.checkedAt)}</Row>
              <Row label="Kanıt (requestIdentifier)">
                {view.requestIdentifier ? (
                  <span className="font-mono">{view.requestIdentifier}</span>
                ) : (
                  <span className="text-[var(--c-warn)]">yok</span>
                )}
              </Row>
              {view.viesName && <Row label="VIES unvanı">{view.viesName}</Row>}
              {view.viesAddress && <Row label="VIES adresi">{view.viesAddress}</Row>}
              {view.error && <Row label="Servis yanıtı">{view.error}</Row>}
            </dl>

            {view.requestIdentifier === null && view.tone === 'ok' && (
              <p className="mt-2 text-xs text-[var(--c-warn)]">
                ⚠ Bu sorgu KANIT üretmedi. VIES'in yasal kanıtı (qualified confirmation) ancak
                istekte kendi KDV numaramız gönderilirse döner — Ayarlar'da{' '}
                <code>company_vat_number</code> tanımlı olmalı.
              </p>
            )}

            {fresh?.stored === false && (
              <p className="mt-2 text-xs text-[var(--c-ink-soft)]">
                Sonuç cariye YAZILMADI: numara ayrıştırılamadığı için uydurma bir kayıt kurulmuyor.
              </p>
            )}
          </div>
        )}

        {/*
          Bu not KALICIDIR ve kaldırılmamalıdır: operatör buradaki yeşil rozeti
          "kanıtım var" diye okuyabilir. Kutu 44/46'nın maddi şartı TESLİM
          ANINDAKİ geçerliliktir ve o kanıt faturaya donar, bu satıra değil.
        */}
        <p className="border-t border-[var(--c-border)] pt-3 text-xs text-[var(--c-ink-faint)]">
          Bu kayıt <strong>anlık</strong> bir durumdur ve bir sonraki kontrolde üzerine yazılır.
          Denetimde geçerli olan kanıt, <strong>teslim anında</strong> alınıp{' '}
          <strong>faturaya donan</strong> sorgudur — kutu 44 (AB B2B hizmet) ve kutu 46 (AB'ye mal
          teslimi) muafiyetinin maddi şartı budur.
        </p>
      </div>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-[var(--c-ink-faint)]">{label}:</dt>
      <dd className="text-[var(--c-ink)]">{children}</dd>
    </div>
  );
}

// ──────────────────────────────────────────────────────────── silme kutusu

/**
 * Silme — `CONTACT_HAS_DOCUMENTS` yolu.
 *
 * Faturası olan cari SİLİNEMEZ (`FK_sales_invoices_contact` RESTRICT) ve bu
 * hukukidir: mali bir belgenin gösterdiği hiçbir satır kaybolmamalı. Sunucu
 * bunu bir hata koduyla söyler; panelin işi kodu ÇÖZÜM YOLUNA çevirmektir —
 * pasifleştirme düğmesi doğrudan burada gösterilir.
 */
function DeleteCard({
  contact,
  onDeactivated,
}: {
  contact: Contact;
  /**
   * Açık formdaki "Kayıt aktif" kutusunu da düşürür. Sunucuyu pasifleştirip
   * formu aktif göstermek, operatörün bir sonraki "Kaydet"inde cariyi sessizce
   * GERİ AKTİFLEŞTİRMESİ demek olurdu.
   */
  onDeactivated: () => void;
}) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const remove = useDeleteContact();
  const update = useUpdateContact();

  const deactivate = () => {
    update.mutate(
      { id: contact.id, patch: { active: false } },
      {
        onSuccess: () => {
          setBlocked(false);
          setConfirming(false);
          onDeactivated();
          toast.success('Cari pasifleştirildi. Belgeleri ve geçmişi olduğu gibi duruyor.');
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title="Kaydı sil"
        description="Belgesi olan cari silinemez; doğru yol pasifleştirmedir."
      />
      <div className="space-y-3 px-4 py-4">
        {blocked ? (
          <div className="rounded-[var(--r-md)] bg-[var(--c-danger-soft)] px-3 py-3">
            <p className="text-sm font-medium text-[var(--c-danger)]">
              Faturası olan cari silinemez — PASİFLEŞTİRİN.
            </p>
            <p className="mt-1 text-xs text-[var(--c-ink-soft)]">
              Bu cariye ait düzenlenmiş belgeler var. Mali bir belgenin gösterdiği hiçbir satır
              silinmez; kayıt pasifleştirilir ve listelerde görünmez olur, belgeler yerinde kalır.
            </p>
            {contact.active ? (
              <Button
                className="mt-3"
                size="sm"
                variant="secondary"
                loading={update.isPending}
                onClick={deactivate}
              >
                Pasifleştir
              </Button>
            ) : (
              <p className="mt-3 text-xs text-[var(--c-ink-soft)]">Bu cari zaten pasif.</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-[var(--c-ink-soft)]">
            Hiç belgesi olmayan bir cari kalıcı olarak silinebilir. Belgesi varsa sunucu silmeyi
            reddeder ve burada pasifleştirme yolu açılır.
          </p>
        )}

        <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>
          Cariyi sil
        </Button>

        <Modal
          open={confirming}
          onClose={() => setConfirming(false)}
          title="Cari silinsin mi?"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Vazgeç
              </Button>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() =>
                  remove.mutate(contact.id, {
                    onSuccess: () => {
                      setConfirming(false);
                      toast.success('Cari silindi.');
                      // Kayıt artık yok; bu sayfa 404 gösterirdi.
                      void navigate('/contacts', { replace: true });
                    },
                    onError: (err) => {
                      setConfirming(false);
                      if (hasCode(err, 'CONTACT_HAS_DOCUMENTS')) {
                        setBlocked(true);
                        toast.errorText(
                          'Faturası olan cari silinemez. Bunun yerine pasifleştirin.',
                        );
                        return;
                      }
                      toast.error(err);
                    },
                  })
                }
              >
                Evet, sil
              </Button>
            </>
          }
        >
          <p className="text-sm text-[var(--c-ink)]">
            <strong>{contact.name}</strong> kalıcı olarak silinecek. Bu geri alınamaz.
          </p>
          <p className="mt-2 text-xs text-[var(--c-ink-soft)]">
            Cariye ait bir fatura ya da kredi notu varsa sunucu silmeyi reddeder — o durumda
            pasifleştirme seçeneği gösterilir.
          </p>
        </Modal>
      </div>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────── yeni cari

/**
 * "Yeni cari" kutusu.
 *
 * Yalnız KDV çözümleyicisinin ihtiyaç duyduğu alanları sorar; adres, banka ve
 * gelişmiş ayarlar kayıt açıldıktan sonra detay ekranında tamamlanır. Sebep:
 * kaydı açmayı geciktiren her zorunlu alan operatörü "sonra doldururum"
 * diyerek yanlış değer girmeye iter ve o yanlış değer KDV kutusunu belirler.
 */
export function NewContactModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (contact: Contact) => void;
}) {
  const [form, setForm] = useState<ContactForm>(blankForm);
  const [invalid, setInvalid] = useState<Set<string>>(new Set());
  const create = useCreateContact();

  const set = <K extends keyof ContactForm>(key: K, value: ContactForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submit = () => {
    if (form.name.trim() === '') {
      setInvalid(new Set(['name']));
      toast.errorText('Ad zorunludur.');
      return;
    }
    if (!/^[A-Za-z]{2}$/.test(form.countryCode.trim())) {
      setInvalid(new Set(['countryCode']));
      toast.errorText('Ülke kodu iki harf olmalı.');
      return;
    }
    if (!form.isCustomer && !form.isSupplier) {
      setInvalid(new Set(['isCustomer', 'isSupplier']));
      toast.errorText('Cari en az müşteri ya da tedarikçi olmalı.');
      return;
    }
    if (!paymentDaysValid(form.paymentTermDays)) {
      setInvalid(new Set(['paymentTermDays']));
      toast.errorText('Ödeme vadesi 0–365 arası bir tam sayı olmalı.');
      return;
    }

    create.mutate(buildCreateBody(form), {
      onSuccess: (saved) => {
        toast.success('Cari oluşturuldu. Adres ve banka bilgilerini burada tamamlayabilirsiniz.');
        onCreated(saved);
      },
      onError: (err) => {
        setInvalid(errorFields(err));
        toast.error(err);
      },
    });
  };

  const fields: FieldsProps = { form, set, invalid, disabled: create.isPending };

  return (
    <Modal
      open
      onClose={onClose}
      title="Yeni cari"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button loading={create.isPending} onClick={submit}>
            Oluştur
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <IdentityFields {...fields} />

        <div className="border-t border-[var(--c-border)] pt-3">
          <h3 className="mb-2 text-[13px] font-semibold text-[var(--c-ink)]">KDV nitelendirmesi</h3>
          <VatQualificationFields {...fields} />
        </div>

        <div className="border-t border-[var(--c-border)] pt-3 space-y-3">
          <Input
            label="E-posta"
            type="email"
            value={form.email}
            disabled={create.isPending}
            onChange={(e) => set('email', e.target.value)}
            error={fieldError(invalid, 'email')}
          />
          <Input
            label="Ödeme vadesi (gün)"
            type="number"
            min={0}
            max={365}
            value={form.paymentTermDays}
            disabled={create.isPending}
            onChange={(e) => set('paymentTermDays', e.target.value)}
            error={fieldError(invalid, 'paymentTermDays')}
            hint="B2B'de sözleşmeyle azami 60 gün (WER Kitap VI)."
          />
        </div>

        <p className="text-xs text-[var(--c-ink-faint)]">
          Adres, banka hesabı ve KDV muamelesi override'ı kayıt açıldıktan sonra detay ekranında
          doldurulur.
        </p>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────── detay sayfası

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const readOnly = isReadOnly(user);

  const contactQuery = useContact(id);
  const contact = contactQuery.data ?? null;

  if (!id) return <ErrorState message="Cari kimliği yok." />;
  if (contactQuery.isPending) {
    return (
      <div className="py-20 text-center text-[var(--c-ink-soft)]">
        <Spinner size={22} />
      </div>
    );
  }
  if (contactQuery.isError || contact === null) {
    return (
      <>
        <PageHeader title="Cari" />
        <ErrorState message={errorMessage(contactQuery.error)} />
        <p className="text-center text-sm">
          <Link className="text-[var(--c-brand)] underline" to="/contacts">
            Cari listesine dön
          </Link>
        </p>
      </>
    );
  }

  return <ContactEditor key={contact.id} contact={contact} readOnly={readOnly} />;
}

/**
 * ⚠ `key={contact.id}` ile monte edilir: form durumu carinin İLK yüklenen
 * hâlinden kurulur ve sonraki arka plan tazelemeleri operatörün yazdığını
 * EZMEZ. Kimlik değişince bileşen baştan kurulur.
 */
function ContactEditor({ contact, readOnly }: { contact: Contact; readOnly: boolean }) {
  const [form, setForm] = useState<ContactForm>(() => formFrom(contact));
  const [invalid, setInvalid] = useState<Set<string>>(new Set());
  const update = useUpdateContact();

  const set = <K extends keyof ContactForm>(key: K, value: ContactForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const fields: FieldsProps = { form, set, invalid, disabled: readOnly || update.isPending };

  const save = () => {
    if (!paymentDaysValid(form.paymentTermDays)) {
      setInvalid(new Set(['paymentTermDays']));
      toast.errorText('Ödeme vadesi 0–365 arası bir tam sayı olmalı.');
      return;
    }

    const patch = buildPatch(form, contact);
    if (Object.keys(patch).length === 0) {
      toast.info('Değişiklik yok.');
      return;
    }

    update.mutate(
      { id: contact.id, patch },
      {
        onSuccess: (saved) => {
          setInvalid(new Set());
          // Sunucu değerleri kanonikleştirir; formu YANITTAN kuruyoruz ki
          // operatör kaydedilen gerçek değeri görsün.
          setForm(formFrom(saved));
          toast.success('Cari güncellendi.');
        },
        onError: (err) => {
          setInvalid(errorFields(err));
          toast.error(err);
        },
      },
    );
  };

  return (
    <>
      <PageHeader
        title={contact.name}
        description={contact.legalName ?? undefined}
        actions={
          <>
            <Link className="text-sm text-[var(--c-brand)] underline" to="/contacts">
              Listeye dön
            </Link>
            {!readOnly && (
              <Button loading={update.isPending} onClick={save}>
                Kaydet
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge tone={contact.active ? 'ok' : 'neutral'}>{contact.active ? 'aktif' : 'pasif'}</Badge>
        {contact.isCustomer && <Badge tone="brand">müşteri</Badge>}
        {contact.isSupplier && <Badge tone="brand">tedarikçi</Badge>}
        {contact.customerType && (
          <Badge tone="neutral">{CUSTOMER_TYPE_LABELS[contact.customerType]}</Badge>
        )}
        <Badge tone="neutral">{countryLabel(contact.countryCode)}</Badge>
        {readOnly && <Badge tone="warn">salt okuma</Badge>}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Card>
            <CardHeader title="Kimlik" />
            <div className="px-4 py-4">
              <IdentityFields {...fields} />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="KDV nitelendirmesi"
              description="Çözümleyicinin cari tarafı girdisi — kutu 3 / 44 / 46 ayrımı buradan çıkar."
            />
            <div className="px-4 py-4">
              <VatQualificationFields {...fields} />
            </div>
          </Card>

          <Card>
            <CardHeader title="Adres" description="EN 16931 BG-5; M2'de UBL'e birebir gider." />
            <div className="space-y-3 px-4 py-4">
              <Input
                label="Adres satırı 1"
                value={form.addressLine1}
                disabled={fields.disabled}
                onChange={(e) => set('addressLine1', e.target.value)}
              />
              <Input
                label="Adres satırı 2"
                value={form.addressLine2}
                disabled={fields.disabled}
                onChange={(e) => set('addressLine2', e.target.value)}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Posta kodu"
                  value={form.postalCode}
                  disabled={fields.disabled}
                  onChange={(e) => set('postalCode', e.target.value)}
                />
                <Input
                  label="Şehir"
                  value={form.city}
                  disabled={fields.disabled}
                  onChange={(e) => set('city', e.target.value)}
                />
              </div>
              <Input
                label="İl / eyalet"
                value={form.countrySubdivision}
                disabled={fields.disabled}
                onChange={(e) => set('countrySubdivision', e.target.value)}
                hint="EN 16931 BT-39. Belçika'da genelde boş bırakılır."
              />
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <ViesPanel contact={contact} canWrite={!readOnly} />

          <Card>
            <CardHeader title="İletişim ve ödeme" />
            <div className="space-y-3 px-4 py-4">
              <Input
                label="E-posta"
                type="email"
                value={form.email}
                disabled={fields.disabled}
                onChange={(e) => set('email', e.target.value)}
                error={fieldError(invalid, 'email')}
              />
              <Input
                label="Telefon"
                value={form.phone}
                disabled={fields.disabled}
                onChange={(e) => set('phone', e.target.value)}
              />
              <Input
                label="Ödeme vadesi (gün)"
                type="number"
                min={0}
                max={365}
                value={form.paymentTermDays}
                disabled={fields.disabled}
                onChange={(e) => set('paymentTermDays', e.target.value)}
                error={fieldError(invalid, 'paymentTermDays')}
                hint="B2B'de sözleşmeyle azami 60 gün (WER Kitap VI). Faturada vade bundan türer."
              />
              <Input
                label="IBAN"
                value={form.iban}
                disabled={fields.disabled}
                onChange={(e) => set('iban', e.target.value)}
                error={fieldError(invalid, 'iban')}
                hint="⚠ Bu CARİNİN hesabıdır. 'BE ile başlamalı' kuralı (WER md. III.25 §2) yalnız BİZİM ödeme talep eden belgelerimizdeki kendi hesabımız içindir; tedarikçinin yabancı hesabı meşrudur."
              />
              <Input
                label="BIC"
                value={form.bic}
                disabled={fields.disabled}
                onChange={(e) => set('bic', e.target.value)}
                error={fieldError(invalid, 'bic')}
              />
            </div>
          </Card>

          {/*
            ⚠⚠ PEPPOL DIŞI KANALIN DAYANAĞI. Yurtiçi B2B'de yapılandırılmış
            e-fatura zorunlu ve standart kanal Peppol'dür; e-postayla göndermek
            ancak İKİ TARAF ANLAŞIRSA mümkündür. Bu üç alan o anlaşmanın
            kaydıdır — denetimde sorulacak olan da tam olarak budur.
          */}
          <Card>
            <CardHeader
              title="E-posta ile e-fatura"
              description="Müşteri faturayı e-postayla almayı kabul ettiyse doldurun. Üçü birden dolar ya da üçü birden boş kalır."
            />
            <div className="space-y-3 px-4 py-4">
              <Input
                label="E-fatura adresi"
                type="email"
                value={form.einvoiceEmailAddress}
                disabled={fields.disabled}
                onChange={(e) => set('einvoiceEmailAddress', e.target.value)}
                error={fieldError(invalid, 'einvoiceEmailAddress')}
                hint="Fatura bu adrese gider: ekte UBL (faturanın kendisi) ve PDF (okunur kopya)."
              />
              <Input
                label="Onay tarihi"
                type="date"
                value={form.einvoiceEmailConsentAt}
                disabled={fields.disabled}
                onChange={(e) => set('einvoiceEmailConsentAt', e.target.value)}
                error={fieldError(invalid, 'einvoiceEmailConsentAt')}
                hint="Müşterinin kabul ettiği gün. Gelecek tarih kabul edilmez: kanalın dayanağı o günden önce yoktur."
              />
              <Textarea
                label="Onay nerede verildi"
                value={form.einvoiceEmailConsentNote}
                rows={2}
                disabled={fields.disabled}
                onChange={(e) => set('einvoiceEmailConsentNote', e.target.value)}
                error={fieldError(invalid, 'einvoiceEmailConsentNote')}
                hint="Örn. “Sözleşme md. 4” ya da “10.09.2026 tarihli e-posta”. Denetimde tek açıklama budur."
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Gelişmiş"
              description="Günlük kullanımda dokunulmaz — buradaki seçim otomatik çözümü ezer."
            />
            <div className="space-y-4 px-4 py-4">
              <AdvancedVatField form={form} set={set} disabled={fields.disabled} />

              <div className="border-t border-[var(--c-border)] pt-3">
                <Input
                  label="Peppol katılımcı kimliği"
                  value={form.peppolParticipantId}
                  disabled={fields.disabled}
                  onChange={(e) => set('peppolParticipantId', e.target.value)}
                  error={fieldError(invalid, 'peppolParticipantId')}
                  hint="Boş bırakılırsa Belçika işletme numarasından 0208: şemasıyla türetilir. Elle yazılan değere sunucu DOKUNMAZ — yanlış kimlik e-faturayı sessizce yanlış alıcıya gönderir."
                />
                <p className="mt-2 text-xs text-[var(--c-ink-soft)]">
                  SMP durumu:{' '}
                  {contact.peppolCapable === null
                    ? 'hiç bakılmadı'
                    : contact.peppolCapable
                      ? 'Peppol ağında bulundu'
                      : 'Peppol ağında bulunamadı'}
                  {contact.peppolCheckedAt && ` — ${dateTime(contact.peppolCheckedAt)}`}
                  {contact.peppolIncapableReason && ` (${contact.peppolIncapableReason})`}
                </p>
                <p className="mt-1 text-xs text-[var(--c-ink-faint)]">
                  Kimlik değiştirilirse SMP cevabı sıfırlanır: eski cevap ARTIK BAŞKA BİR kimlik
                  hakkındadır.
                </p>
              </div>

              <div className="border-t border-[var(--c-border)] pt-3">
                <Checkbox
                  label="Kayıt aktif"
                  disabled={fields.disabled}
                  checked={form.active}
                  onChange={(e) => set('active', e.target.checked)}
                  hint="Pasif cari listelerde gizlenir ama belgeleri ve geçmişi yerinde kalır. Silmenin yerine bu kullanılır."
                />
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Notlar" description="İç not — faturaya basılmaz." />
            <div className="px-4 py-4">
              <Textarea
                value={form.notes}
                rows={4}
                disabled={fields.disabled}
                onChange={(e) => set('notes', e.target.value)}
              />
            </div>
          </Card>

          {!readOnly && (
            <DeleteCard
              contact={contact}
              onDeactivated={() => setForm((prev) => ({ ...prev, active: false }))}
            />
          )}

          <p className="text-xs text-[var(--c-ink-faint)]">
            Oluşturma: {dateTime(contact.createdAt)} · Son değişiklik: {dateTime(contact.updatedAt)}
          </p>
        </div>
      </div>
    </>
  );
}
